import { Expense, ActivityLog, RecurrenceInterval } from '../types';

/**
 * Calculates the next recurring ISO timestamp based on the given ISO date and recurrence interval.
 */
export const calculateNextRecurringDate = (
  baseDateIso: string,
  recurrence: RecurrenceInterval
): string | undefined => {
  if (!recurrence || recurrence === 'one_time') {
    return undefined;
  }

  const baseDate = new Date(baseDateIso);
  if (isNaN(baseDate.getTime())) {
    return undefined;
  }

  const nextDate = new Date(baseDate);

  switch (recurrence) {
    case 'weekly':
      nextDate.setDate(nextDate.getDate() + 7);
      break;

    case 'monthly': {
      const currentDay = baseDate.getDate();
      nextDate.setMonth(nextDate.getMonth() + 1);
      // Handle edge cases where month has fewer days (e.g. Jan 31 -> Feb 28)
      if (nextDate.getDate() !== currentDay) {
        nextDate.setDate(0); // Sets to last day of previous month
      }
      break;
    }

    case 'yearly': {
      const currentMonth = baseDate.getMonth();
      nextDate.setFullYear(nextDate.getFullYear() + 1);
      // Handle leap year Feb 29 edge case
      if (nextDate.getMonth() !== currentMonth) {
        nextDate.setDate(0);
      }
      break;
    }

    default:
      return undefined;
  }

  return nextDate.toISOString();
};

/**
 * Checks if a recurring expense is due for auto-cloning.
 */
export const isRecurrenceDue = (expense: Expense, now: Date = new Date()): boolean => {
  if (!expense.recurrence || expense.recurrence === 'one_time') {
    return false;
  }

  // If nextRecurringDate is not yet set, use expense.date as the reference
  const targetDateStr = expense.nextRecurringDate || calculateNextRecurringDate(expense.date, expense.recurrence);
  if (!targetDateStr) return false;

  const targetDate = new Date(targetDateStr);
  return !isNaN(targetDate.getTime()) && targetDate.getTime() <= now.getTime();
};

/**
 * Generates human-friendly recurring schedule description for UI badges and helpers.
 */
export const getRecurrenceHumanText = (
  recurrence: RecurrenceInterval | undefined,
  dateIso: string,
  t: (key: string, options?: any) => string
): string => {
  if (!recurrence || recurrence === 'one_time') {
    return t('recurrence_one_time_desc', { defaultValue: 'Does not repeat' });
  }

  const d = new Date(dateIso);
  const isValidDate = !isNaN(d.getTime());

  switch (recurrence) {
    case 'weekly': {
      const weekday = isValidDate ? d.toLocaleDateString(undefined, { weekday: 'long' }) : '';
      return t('repeats_weekly', { 
        weekday, 
        defaultValue: weekday ? `Repeats weekly on ${weekday}` : 'Repeats weekly' 
      });
    }

    case 'monthly': {
      const day = isValidDate ? d.getDate() : '';
      return t('repeats_monthly', { 
        day, 
        defaultValue: day ? `Repeats monthly on day ${day}` : 'Repeats monthly' 
      });
    }

    case 'yearly': {
      const monthDay = isValidDate ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
      return t('repeats_yearly', { 
        date: monthDay 
      });
    }

    default:
      return '';
  }
};

export interface ProcessedRecurrenceResult {
  newExpenses: Expense[];
  updatedParents: { id: string; nextRecurringDate: string }[];
  newActivities: ActivityLog[];
}

/**
 * Processes all eligible recurring expenses, advancing their dates and generating cloned instances
 * up to the current date/time. Caps at 12 iterations per expense to prevent runaway loops.
 */
export const processDueRecurringExpenses = (
  expenses: Expense[],
  currentUserId: string,
  t: (key: string, options?: any) => string,
  now: Date = new Date()
): ProcessedRecurrenceResult => {
  const result: ProcessedRecurrenceResult = {
    newExpenses: [],
    updatedParents: [],
    newActivities: []
  };

  // Process only primary recurring template expenses (not auto-generated instances)
  const recurringTemplates = expenses.filter(
    e => e.recurrence && e.recurrence !== 'one_time' && !e.isRecurringAuto
  );

  for (const parent of recurringTemplates) {
    let currentNextDateStr = parent.nextRecurringDate || calculateNextRecurringDate(parent.date, parent.recurrence!);
    if (!currentNextDateStr) continue;

    let iterations = 0;
    const MAX_ITERATIONS = 12;

    while (currentNextDateStr && new Date(currentNextDateStr).getTime() <= now.getTime() && iterations < MAX_ITERATIONS) {
      iterations++;
      const occurrenceDate = currentNextDateStr;
      const isPayment = parent.type === 'payment' || parent.category === 'payment';
      const newExpenseId = `rec_${parent.id}_${new Date(occurrenceDate).getTime()}`;

      // Avoid duplicating if this exact recurring occurrence already exists
      const alreadyExists = expenses.some(e => e.id === newExpenseId) || 
                            result.newExpenses.some(e => e.id === newExpenseId);

      if (!alreadyExists) {
        const newExpense: Expense = {
          ...parent,
          id: newExpenseId,
          date: occurrenceDate,
          isRecurringAuto: true,
          recurringParentId: parent.id,
          recurrence: parent.recurrence,
          nextRecurringDate: undefined // Clones do not schedule themselves; the parent does
        };
        result.newExpenses.push(newExpense);

        // Construct auto-recurring activity log
        const recurrenceLabel = parent.recurrence ? parent.recurrence.toUpperCase() : 'RECURRING';
        const actionText = isPayment 
          ? `[Auto ${parent.recurrence}] ${t('settlement')}` 
          : `[Auto ${parent.recurrence}] ${t('action_added')} ${t('expense')}`;

        const newActivity: ActivityLog = {
          id: `act_rec_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
          userId: parent.payerId || currentUserId,
          action: actionText,
          details: `${parent.description} (${recurrenceLabel})`,
          timestamp: new Date().toISOString(),
          relatedUserIds: Array.from(new Set([parent.payerId, ...(parent.splitWith || [])].filter(Boolean))),
          groupId: parent.groupId,
          recurrence: parent.recurrence,
          isRecurringAuto: true,
          expenseId: newExpense.id
        };
        result.newActivities.push(newActivity);
      }

      // Advance to next period
      currentNextDateStr = calculateNextRecurringDate(occurrenceDate, parent.recurrence!);
    }

    if (currentNextDateStr && currentNextDateStr !== parent.nextRecurringDate) {
      result.updatedParents.push({
        id: parent.id,
        nextRecurringDate: currentNextDateStr
      });
    }
  }

  return result;
};
